import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";

const categorySchema = z.object({
  nome: z.string().min(1, "Nome é obrigatório"),
});

type CategoryFormData = z.infer<typeof categorySchema>;

interface QuickAddCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCategoryCreated?: (categoryName: string, categoryId?: string) => void;
}

export function QuickAddCategoryModal({ open, onOpenChange, onCategoryCreated }: QuickAddCategoryModalProps) {
  const queryClient = useQueryClient();
  const { tenantId, loading: tenantLoading } = useTenant();

  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      nome: "",
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: async (data: CategoryFormData) => {
      if (!tenantId) {
        throw new Error("Nenhuma empresa selecionada");
      }

      // Inserir categoria; se já existir (violação de unique), retornar a existente
      const { data: inserted, error } = await (supabase as any)
        .from("expense_categories")
        .insert([
          { nome: data.nome, tenant_id: tenantId },
        ])
        .select("id, nome")
        .single();

      if (error) {
        // Tentar buscar existente por nome/tenant (duplicado)
        const { data: existing } = await (supabase as any)
          .from("expense_categories")
          .select("id, nome")
          .eq("tenant_id", tenantId)
          .eq("nome", data.nome)
          .maybeSingle();
        if (existing) return existing as any;
        throw error;
      }
      return inserted as any;
    },
    onSuccess: (category: any) => {
      toast.success("Categoria cadastrada com sucesso!");
      queryClient.invalidateQueries({ queryKey: ["expense-categories", tenantId] });
      if (category?.id) {
        void logCrud({ entitySlug: "entity:expense_categories", operationSlug: "create", entityId: category.id, entityName: category.nome, screenSlug: "categorias-despesa" });
      }
      if (category) {
        queryClient.setQueryData(["expense-categories", tenantId], (current: any) => {
          if (!current) {
            return [category];
          }
          const exists = current.some((item: any) => item?.id === category?.id);
          return exists ? current : [category, ...current];
        });
        onCategoryCreated?.(category?.nome, category?.id);
      }
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error("Erro ao cadastrar categoria: " + error.message);
    },
  });

  const onSubmit = (data: CategoryFormData) => {
    if (!tenantId) {
      toast.error("Selecione uma empresa antes de cadastrar categorias.");
      return;
    }
    createCategoryMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex max-h-[80vh] flex-col">
          <DialogHeader className="sticky top-0 z-10 bg-background px-6 py-4 border-b relative">
            <DialogTitle>Cadastro Rápido de Categoria</DialogTitle>
            <DialogDescription>
              Cadastre uma nova categoria para organizar melhor suas despesas.
            </DialogDescription>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <Form {...form}>
              <form id="quick-category-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

              </form>
            </Form>
          </div>

          <div className="bg-background px-6 py-4 border-t flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="quick-category-form" disabled={createCategoryMutation.isPending || tenantLoading || !tenantId}>
              {createCategoryMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Cadastrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
